import { io } from 'socket.io-client';

class WebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
  }

  // Conectar ao servidor WebSocket
  connect(token = null) {
    if (this.socket && this.isConnected) {
      return;
    }

    const serverUrl = process.env.NODE_ENV === 'production'
      ? 'https://hml.ebdad.com.br'
      : 'http://localhost:8002';


    this.socket = io(serverUrl, {
      auth: token ? { token } : undefined,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: this.reconnectDelay,
      timeout: 20000
    });

    this.setupEventHandlers();
  }

  // Configurar handlers de eventos
  setupEventHandlers() {
    if (!this.socket) return;

    // Eventos de conexão
    this.socket.on('connect', () => {
      this.isConnected = true;
      this.reconnectAttempts = 0;
      
      // Autenticar se temos token
      const token = localStorage.getItem('ebd_token');
      if (token) {
        this.authenticate(token);
      }

      // Notificar listeners sobre conexão
      this.emit('connection_status', { connected: true });
    });

    this.socket.on('disconnect', (reason) => {
      this.isConnected = false;
      this.emit('connection_status', { connected: false, reason });
    });

    // Eventos de autenticação
    this.socket.on('authenticated', (data) => {
      this.emit('authenticated', data);
    });

    this.socket.on('auth_error', (data) => {
      this.emit('auth_error', data);
    });

    // Eventos de dados em tempo real
    this.socket.on('data_update', (data) => {
      this.handleDataUpdate(data);
    });

    // Eventos de erro
    this.socket.on('connect_error', (error) => {
      this.handleReconnect();
    });

    this.socket.on('reconnect', (attemptNumber) => {
      this.reconnectAttempts = 0;
    });

    this.socket.on('reconnect_failed', () => {
    });
  }

  // Autenticar com token
  authenticate(token) {
    if (this.socket && this.isConnected) {
      this.socket.emit('authenticate', { token });
    }
  }

  // Processar updates de dados
  handleDataUpdate(data) {
    const { type, data: payload, timestamp } = data;
    
    // Emitir evento específico baseado no tipo
    this.emit('data_update', data);
    this.emit(`data_update:${type}`, payload);

    // Mostrar notificação se disponível
    if (payload.message && this.shouldShowNotification(type)) {
      this.showNotification(payload.message, type);
    }
  }

  // Verificar se deve mostrar notificação
  shouldShowNotification(type) {
    // Não mostrar notificações para updates muito frequentes
    const quietTypes = ['attendance_bulk', 'sync'];
    return !quietTypes.includes(type);
  }

  // Mostrar notificação do browser
  showNotification(message, type) {
    if ('Notification' in window && Notification.permission === 'granted') {
      const iconMap = {
        'student_created': '👨‍🎓',
        'student_deleted': '❌',
        'attendance_updated': '✅',
        'turma_created': '📚',
        'default': '📱'
      };

      new Notification(`EBD Manager - ${iconMap[type] || iconMap.default}`, {
        body: message,
        icon: '/logo_belem.png',
        tag: 'ebd-update',
        requireInteraction: false
      });
    }
  }

  // Tentar reconectar
  handleReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
      
      
      setTimeout(() => {
        if (!this.isConnected) {
          const token = localStorage.getItem('ebd_token');
          this.connect(token);
        }
      }, delay);
    }
  }

  // Adicionar listener para eventos
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
  }

  // Remover listener
  off(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
    }
  }

  // Emitir evento para listeners
  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
        }
      });
    }
  }

  // Desconectar
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.listeners.clear();
      this.reconnectAttempts = 0;
    }
  }

  // Verificar status da conexão
  getConnectionStatus() {
    return {
      connected: this.isConnected,
      socketId: this.socket?.id,
      reconnectAttempts: this.reconnectAttempts
    };
  }

  // Solicitar permissão para notificações
  static async requestNotificationPermission() {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return Notification.permission === 'granted';
  }
}

// Instância singleton
const webSocketService = new WebSocketService();

export default webSocketService;
export { WebSocketService };